import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { Loader2, ArrowLeft, MessageSquare, Edit3, Save } from 'lucide-react';
import { toast } from 'sonner';

const pctColor = (p) => p >= 70 ? 'text-emerald-600 bg-emerald-50' : p >= 40 ? 'text-amber-600 bg-amber-50' : 'text-rose-600 bg-rose-50';

export function TeacherSubmissionsPage() {
    const [feed, setFeed] = useState(null);
    const [classes, setClasses] = useState([]);
    const [filterClass, setFilterClass] = useState('');

    useEffect(() => {
        api.get('/api/teacher/classrooms').then((r) => setClasses(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        const url = '/api/teacher/submissions' + (filterClass ? `?classroom_id=${filterClass}` : '');
        api.get(url).then((r) => setFeed(r.data?.items || [])).catch(() => setFeed([]));
    }, [filterClass]);

    return (
        <div className="space-y-6" data-testid="submissions-feed-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Submissions</h1>
                    <p className="text-sm text-zinc-500 mt-1">Live feed of every grade across your classes.</p>
                </div>
                <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm" data-testid="filter-classroom">
                    <option value="">All classes</option>
                    {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </div>

            {feed === null ? (
                <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 text-[#7C3AED] animate-spin" /></div>
            ) : feed.length === 0 ? (
                <div className="bg-white rounded-3xl border-2 border-dashed border-zinc-200 p-12 text-center" data-testid="empty-feed">
                    <h3 className="text-lg font-black mb-1">No submissions yet</h3>
                    <p className="text-sm text-zinc-500">When your students grade work in the app, you'll see it here.</p>
                </div>
            ) : (
                <ul className="space-y-2">
                    {feed.map((s) => (
                        <li key={s.id}>
                            <Link to={`/teacher/submissions/${s.id}`} className="block bg-white rounded-2xl border border-zinc-200 p-4 hover:border-[#7C3AED]/40 transition-colors" data-testid={`feed-${s.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs text-zinc-400 mb-1">
                                            {s.subject}
                                            {s.paper_year ? ` · Paper ${s.paper_year}` : ''}
                                            {s.series ? ` ${s.series}` : ''}
                                            {s.question_number ? ` · Q${s.question_number}` : ''}
                                            {' · '}{s.submitted_at ? new Date(s.submitted_at).toLocaleString() : ''}
                                        </div>
                                        <div className="font-bold text-sm truncate">{s.student_name || 'Student'}</div>
                                        <div className="text-xs text-zinc-500 truncate mt-0.5">{s.question_snippet}</div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <span className={`text-xs font-black px-2 py-1 rounded-md ${pctColor(s.pct)}`}>{Math.round(s.pct)}%</span>
                                        {s.teacher_score != null && <span className="text-[10px] font-bold text-[#7C3AED]">Teacher: {s.teacher_score}/{s.max_score}</span>}
                                        {s.has_comment && <MessageSquare className="w-3 h-3 text-[#7C3AED]" />}
                                    </div>
                                </div>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export function TeacherSubmissionDetailPage() {
    const { submissionId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [comment, setComment] = useState('');
    const [visible, setVisible] = useState(false);
    const [showOverride, setShowOverride] = useState(false);
    const [score, setScore] = useState('');
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    const reload = () => {
        api.get(`/api/teacher/submissions/${submissionId}`).then((r) => {
            setData(r.data);
            setComment(r.data.teacher_comment || '');
            setVisible(r.data.teacher_comment_visible_to_student || false);
            setScore(r.data.teacher_score != null ? String(r.data.teacher_score) : '');
            setReason(r.data.teacher_score_reason || '');
        }).catch(() => setData(false));
    };
    useEffect(reload, [submissionId]);

    if (data === null) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 text-[#7C3AED] animate-spin" /></div>;
    if (data === false) return <div className="text-sm text-zinc-500">Submission not found.</div>;

    const saveComment = async () => {
        if (!comment.trim()) return toast.error('Comment cannot be empty');
        setSaving(true);
        try {
            await api.post(`/api/teacher/submissions/${submissionId}/comment`, { comment, visible_to_student: visible });
            toast.success('Comment saved');
            reload();
        } catch { toast.error('Could not save comment'); }
        finally { setSaving(false); }
    };

    const saveOverride = async () => {
        if (!score || !reason.trim()) return toast.error('Score and reason required');
        setSaving(true);
        try {
            await api.post(`/api/teacher/submissions/${submissionId}/override`, { teacher_score: parseInt(score, 10), reason });
            toast.success('Score overridden');
            setShowOverride(false);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not override');
        } finally { setSaving(false); }
    };

    return (
        <div className="space-y-6 max-w-4xl" data-testid="submission-detail-page">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm font-bold text-zinc-500 hover:text-[#7C3AED]" data-testid="back-submissions">
                <ArrowLeft className="w-4 h-4" /> Back
            </button>

            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-[#7C3AED]">
                        {data.subject}
                        {data.paper_year ? ` · Paper ${data.paper_year}` : ''}
                        {data.series ? ` ${data.series}` : ''}
                        {data.question_number ? ` · Q${data.question_number}` : ''}
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-black tracking-tight">{data.student_name}</h1>
                    <div className="text-sm text-zinc-500 mt-1">{data.student_email}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                    <span className={`text-sm font-black px-3 py-1.5 rounded-md ${pctColor(data.pct)}`}>AI: {data.score}/{data.max_score} ({Math.round(data.pct)}%)</span>
                    {data.teacher_score != null && <span className="text-xs font-bold text-[#7C3AED]">Teacher: {data.teacher_score}/{data.max_score}</span>}
                </div>
            </div>

            {/* Question + Answer */}
            <Section title="Question">
                <p className="text-sm text-zinc-700 whitespace-pre-wrap">{data.question}</p>
            </Section>
            <Section title="Student answer">
                <p className="text-sm text-zinc-700 whitespace-pre-wrap">{data.student_answer || <em>(uploaded as image)</em>}</p>
            </Section>

            {/* AI Feedback */}
            <Section title="AI mark scheme feedback">
                <p className="text-sm text-zinc-700 mb-3 whitespace-pre-wrap">{data.feedback_summary}</p>
                {data.missing_points?.length > 0 && (
                    <>
                        <div className="text-xs font-bold text-rose-600 mb-1">Missing / improvement points</div>
                        <ul className="list-disc pl-5 text-sm text-zinc-700 space-y-1 mb-3">
                            {data.missing_points.map((m, i) => <li key={i}>{m}</li>)}
                        </ul>
                    </>
                )}
                {data.improvement_tips?.length > 0 && (
                    <>
                        <div className="text-xs font-bold text-amber-600 mb-1">Tips</div>
                        <ul className="list-disc pl-5 text-sm text-zinc-700 space-y-1">
                            {data.improvement_tips.map((m, i) => <li key={i}>{m}</li>)}
                        </ul>
                    </>
                )}
            </Section>

            {/* Teacher comment */}
            <Section title="Your comment" icon={<MessageSquare className="w-4 h-4 text-[#7C3AED]" />}>
                <textarea
                    value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
                    placeholder="Write a comment for this submission…"
                    className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 outline-none focus:border-[#7C3AED] text-sm" data-testid="teacher-comment"
                />
                <label className="flex items-center gap-2 mt-2 text-xs text-zinc-600">
                    <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} data-testid="comment-visible-toggle" />
                    Visible to student
                </label>
                <button onClick={saveComment} disabled={saving} className="mt-3 inline-flex items-center gap-2 bg-[#7C3AED] text-white font-bold text-xs px-4 py-2 rounded-lg hover:bg-[#6D28D9] disabled:opacity-60" data-testid="save-comment">
                    <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save comment'}
                </button>
            </Section>

            {/* Override */}
            <Section title="Override AI score" icon={<Edit3 className="w-4 h-4 text-amber-500" />}>
                {!showOverride ? (
                    <button onClick={() => setShowOverride(true)} className="text-sm font-bold text-[#7C3AED]" data-testid="show-override">
                        {data.teacher_score != null ? 'Adjust override' : 'Disagree with AI? Override the score'}
                    </button>
                ) : (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <input type="number" min="0" max={data.max_score} value={score} onChange={(e) => setScore(e.target.value)} className="w-24 px-3 py-2 rounded-lg border border-zinc-200 text-sm" data-testid="override-score" placeholder="Score" />
                            <span className="text-sm text-zinc-500">/ {data.max_score}</span>
                        </div>
                        <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for override (required)" className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm" data-testid="override-reason" />
                        <div className="flex items-center gap-2">
                            <button onClick={saveOverride} disabled={saving} className="bg-amber-500 text-white font-bold text-xs px-4 py-2 rounded-lg hover:bg-amber-600 disabled:opacity-60" data-testid="save-override">Save override</button>
                            <button onClick={() => setShowOverride(false)} className="text-xs font-bold text-zinc-500 px-3 py-2">Cancel</button>
                        </div>
                    </div>
                )}
                {data.teacher_score_reason && !showOverride && (
                    <p className="text-xs text-zinc-500 mt-2 italic">Last override reason: {data.teacher_score_reason}</p>
                )}
            </Section>
        </div>
    );
}

function Section({ title, icon, children }) {
    return (
        <div className="bg-white rounded-2xl border border-zinc-200 p-5">
            <h3 className="font-black text-sm mb-3 inline-flex items-center gap-2">{icon}{title}</h3>
            {children}
        </div>
    );
}
